import { useEffect, useRef } from "react";
import TagManager from "@sooro-io/react-gtm-module";
import * as Sentry from "@sentry/react";

import { useCreateAccountQuery } from "./createAccountQuery";

import { setUinfoCookieForTagManager } from "@config/gtm";

const storageKey = "checkout_confirmation_tracked_order_ids";

const getStorageValue = (): number[] => {
  const value = localStorage.getItem(storageKey);
  if (!value) return [];
  try {
    return JSON.parse(value);
  } catch (e) {
    Sentry.captureException(e);
    return [];
  }
};

const checkIfOrderIdAlreadyTracked = (orderId: number): boolean => {
  return getStorageValue().includes(orderId);
};

const recordOrderIdAlreadyTracked = (orderId: number): void => {
  const current = getStorageValue();
  const newVal = [...current, orderId];
  localStorage.setItem(storageKey, JSON.stringify(newVal));
};

export const useCheckoutTracking = (): void => {
  const { data: checkoutData } = useCreateAccountQuery();

  const hasAlreadyRunRef = useRef(false);

  useEffect(() => {
    if (!checkoutData) {
      return;
    }

    // even though rest only runs once, totally okay to set the uinfo cookie every time
    const userId = checkoutData.order.user.id.toString();
    const accountId = checkoutData.order.user.account_id.toString();
    setUinfoCookieForTagManager({ userId, accountId });

    // only run once. We're banking that when checkoutData changes, it's just a pointer update.
    // there's no reason for the response from the lambda to change
    if (hasAlreadyRunRef.current) {
      return;
    }
    hasAlreadyRunRef.current = true;

    const isAlreadyTracked = checkIfOrderIdAlreadyTracked(
      checkoutData.order.id,
    );
    if (isAlreadyTracked) {
      return;
    }

    const email = checkoutData.shopifyOrder?.email;
    const orderId = checkoutData.order?.id;
    const priceTotal = checkoutData.shopifyOrder?.total_price;
    const sku = checkoutData.order?.program_plan_obj?.sku;
    const planName = checkoutData.order?.program_plan_obj?.plan_name;
    const firstName = checkoutData.shopifyOrder?.customer?.first_name;
    const lastName = checkoutData.shopifyOrder?.customer?.last_name;

    TagManager.dataLayer({
      dataLayer: {
        customerEmail: email,
        customerFirstName: firstName,
        customerLastName: lastName,
        transactionId: orderId,
        transactionAffiliation: "Mightier",
        transactionTotal: priceTotal,
        transactionProducts: [
          {
            sku,
            name: planName,
            price: priceTotal,
            quantity: 1,
          },
        ],
      },
    });

    TagManager.dataLayer({
      dataLayer: { event: "conversion", price: priceTotal },
    });

    recordOrderIdAlreadyTracked(orderId);
  }, [checkoutData]);
};
